import { supabase } from '@/utils/supabase/client';
import { publicAnonKey, supabaseUrl } from '/utils/supabase/info';

export interface VisionLabModelVersion {
  version: number;
  edge_status: string;
  edge_approved_at?: string | null;
}

export interface VisionLabProjectOption {
  id: string;
  name: string;
  study_object?: string | null;
  versions: VisionLabModelVersion[];
}

export interface KitModelAssignment {
  kit_id: string;
  vision_lab_project_id: string | null;
  vision_model_policy: 'latest_approved' | 'pinned';
  vision_model_version: number | null;
}

export interface KitAcquisitionState {
  kit_id: string;
  video: { available: boolean; enabled: boolean };
  sensors: { available: boolean; enabled: boolean };
  updated_at: string | null;
}

export interface VisionAlertRule {
  rule_id?: string;
  name: string;
  class_label: string;
  min_confidence: number;
  metric: 'presence' | 'count' | 'bbox_frame_ratio' | 'mask_frame_ratio' | 'lesion_subject_ratio';
  min_metric: number | null;
  max_metric: number | null;
  min_count: number;
  persistence_hits: number;
  persistence_window: number;
  cooldown_seconds: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  enabled: boolean;
  title?: string | null;
  description?: string | null;
}

export interface KitAlertRulesPayload {
  rules: VisionAlertRule[];
  model: null | { project_id: string; version: number; task: string | null; classes: string[]; edge_deployable: boolean };
}

const request = async <T>(functionName: string, method: 'GET' | 'PATCH' | 'PUT', kitId: string, body?: unknown, query = ''): Promise<T> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error('Sessão expirada. Volta a autenticar-te para gerir a unidade Edge.');
  const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}?kit_id=${encodeURIComponent(kitId)}${query}`, {
    method,
    headers: {
      apikey: publicAnonKey,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload.error ?? `Pedido falhou (${response.status}).`));
  return payload as T;
};

export const loadKitModelAssignment = (kitId: string) => request<{
  assignment: KitModelAssignment;
  projects: VisionLabProjectOption[];
}>('vision-kit-model', 'GET', kitId);

type KitBrowserModelPayload = {
  browser_model: null | { project_id: string; version: number; url: string; classes: string[]; imgsz: number; task: string | null; expires_in_seconds: number };
};

const browserModelCache = new Map<string, { payload: KitBrowserModelPayload; expiresAt: number }>();

export const loadKitBrowserModel = async (kitId: string) => {
  const cached = browserModelCache.get(kitId);
  if (cached && cached.expiresAt > Date.now()) return cached.payload;

  const payload = await request<KitBrowserModelPayload>('vision-kit-model', 'GET', kitId, undefined, '&include_browser_model=1');
  const lifetimeSeconds = payload.browser_model?.expires_in_seconds ?? 0;
  browserModelCache.set(kitId, {
    payload,
    expiresAt: Date.now() + Math.max(30, lifetimeSeconds - 60) * 1000
  });
  return payload;
};

export const saveKitModelAssignment = (kitId: string, assignment: Partial<KitModelAssignment>) =>
  request<{ assignment: KitModelAssignment }>('vision-kit-model', 'PATCH', kitId, { kit_id: kitId, ...assignment });

export const loadKitAcquisition = (kitId: string) =>
  request<{ acquisition: KitAcquisitionState }>('vision-device-control', 'GET', kitId);

export const saveKitAcquisition = (kitId: string, state: { video_enabled?: boolean; sensors_enabled?: boolean }) =>
  request<{ acquisition: KitAcquisitionState }>('vision-device-control', 'PATCH', kitId, { kit_id: kitId, ...state });

export const loadKitAlertRules = (kitId: string) =>
  request<KitAlertRulesPayload>('vision-alert-rules', 'GET', kitId);

export const saveKitAlertRules = (kitId: string, rules: VisionAlertRule[]) =>
  request<KitAlertRulesPayload>('vision-alert-rules', 'PUT', kitId, { kit_id: kitId, rules });
